import { generatedApi } from '@shared/api/generated-api';

export type CreditTransactionType =
  | 'purchase'
  | 'manual_topup'
  | 'chat_charge'
  | 'lead_charge'
  | 'bonus';

export type CreditTransactionData = {
  id: number;
  amount: number;
  type: CreditTransactionType;
  created_at: string;
  comment: string;
};

export type CreditTransactionsListFilters = {
  date_from: string;
  date_to: string;
  types: CreditTransactionType[];
};

export type CreditTransactionsListResponse = {
  offset: number;
  limit: number;
  count: number;
  items: CreditTransactionData[];
};

export type GetCreditTransactionsListArgs = {
  offset: number;
  limit: number;
  organizationId: number;
  filters: CreditTransactionsListFilters;
};

export const creditTransactionsApi = generatedApi.injectEndpoints({
  endpoints: (build) => ({
    getCreditTransactionsList: build.query<
      CreditTransactionsListResponse,
      GetCreditTransactionsListArgs
    >({
      query: ({ organizationId, offset, limit, filters }) => ({
        url: '/credit/transactions/list',
        method: 'POST',
        body: filters,
        params: {
          organization_id: organizationId,
          limit,
          offset,
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const types = queryArgs?.filters?.types ?? [];
        const sortedTypes = [...types].sort();
        return {
          endpointName,
          organizationId: queryArgs?.organizationId ?? null,
          dateFrom: queryArgs?.filters?.date_from ?? null,
          dateTo: queryArgs?.filters?.date_to ?? null,
          types: sortedTypes,
        };
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!newItems?.items) return;

        const isFirstPage = !arg?.offset || arg.offset === 0;
        const existing = isFirstPage ? [] : (currentCache.items ?? []);
        const combined = [...existing, ...newItems.items];

        const uniqueById = new Map<number, (typeof combined)[number]>();
        combined.forEach((item) => uniqueById.set(item.id, item));

        const sorted = Array.from(uniqueById.values()).sort((a, b) =>
          b.created_at.localeCompare(a.created_at),
        );

        currentCache.items = sorted;
        currentCache.count = newItems.count ?? currentCache.count ?? sorted.length;
        currentCache.offset = arg?.offset ?? newItems.offset ?? 0;
        currentCache.limit = arg?.limit ?? newItems.limit ?? currentCache.limit;
      },
      forceRefetch({ currentArg, previousArg }) {
        const serializeTypes = (value?: CreditTransactionType[]) =>
          value ? [...value].sort().join(',') : '';
        const currentTypes = serializeTypes(currentArg?.filters?.types);
        const previousTypes = serializeTypes(previousArg?.filters?.types);
        const currentOrganizationId = currentArg?.organizationId ?? null;
        const previousOrganizationId = previousArg?.organizationId ?? null;
        const currentDateFrom = currentArg?.filters?.date_from ?? '';
        const previousDateFrom = previousArg?.filters?.date_from ?? '';
        const currentDateTo = currentArg?.filters?.date_to ?? '';
        const previousDateTo = previousArg?.filters?.date_to ?? '';

        return (
          currentArg?.offset !== previousArg?.offset ||
          currentArg?.limit !== previousArg?.limit ||
          currentOrganizationId !== previousOrganizationId ||
          currentDateFrom !== previousDateFrom ||
          currentDateTo !== previousDateTo ||
          currentTypes !== previousTypes
        );
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetCreditTransactionsListQuery } = creditTransactionsApi;
